/* eslint-disable max-len */
import React, {useEffect, useMemo, useState} from 'react';
import {Navigate, Route, Routes} from 'react-router-dom';
import './App.scss';
import About from './pages/About';
import Articles from './pages/Articles';
import Contact from './pages/Contact';
import Crowdsourcing from './pages/Crowdsourcing';
import MapPage from './pages/MapPage';
import Team from './pages/Team';
import Helmet from 'react-helmet';
import CookieConsent from 'react-cookie-consent';
import Icon from './components/Icon/Icon';

export enum Category {
  Battery_Manufacturers = 'Battery manufacturers',
  Battery_Parts_Suppliers = 'Battery parts suppliers',
  Battery_Recycling = 'Battery recycling',
  Battery_Assembly = 'Battery assembly',
  EV_Parts_Supplies = 'EV parts suppliers',
  EV_Manufacturing = 'EV manufacturing',
};

export const categoryOptions = [
  {
    value: Category.EV_Manufacturing,
    label: 'EV manufacturing',
    icon: <Icon type={Category.EV_Manufacturing} width={24} />,
  },
  {
    value: Category.EV_Parts_Supplies,
    label: 'EV parts suppliers',
    icon: <Icon type={Category.EV_Parts_Supplies} width={24} />,
  },
  {
    value: Category.Battery_Manufacturers,
    label: 'Battery manufacturers',
    icon: <Icon type={Category.Battery_Manufacturers} width={24} />,
  },
  {
    value: Category.Battery_Parts_Suppliers,
    label: 'Battery parts suppliers',
    icon: <Icon type={Category.Battery_Parts_Suppliers} width={24} />,
  },
  {
    value: Category.Battery_Assembly,
    label: 'Battery assembly',
    icon: <Icon type={Category.Battery_Assembly} width={24} />,
  },
  {
    value: Category.Battery_Recycling,
    label: 'Battery recycling',
    icon: <Icon type={Category.Battery_Recycling} width={24} />,
  },
];

function getCategoryEnum(category: string): Category {
  switch (category?.trim().toLowerCase()) {
    case 'battery manufacturers':
      return Category.Battery_Manufacturers;
    case 'battery parts suppliers':
      return Category.Battery_Parts_Suppliers;
    case 'battery recycling':
      return Category.Battery_Recycling;
    case 'battery assembly':
      return Category.Battery_Assembly;
    case 'ev parts suppliers':
      return Category.EV_Parts_Supplies;
    case 'ev manufacturing':
      return Category.EV_Manufacturing;
    default:
      return null;
  }
}
export interface DataPoint {
  id: string;
  Country: string;
  Investor_country: string;
  Entity: string;
  Location: string;
  Latitude: string;
  Longitude: string;
  coords: [number, number];
  Amount_EUR_mln: string;
  Category: Category;
  Production_status: string;
  Employment: string;
  'Start of production': string;
  Capacity: string;
  Product: string;
  'EV model and type': string;
  Note: string;
  [otherOptions: string]: unknown;
}

export interface NavigateMethods {
  setCountry: (country: string) => void;
  setInstitution: (institution: string) => void;
  setLink: (link: string) => void;
}

export interface GroupedData {
  [location: string]: {
    country: string;
    entities: InstitutionsGroup
  };
}
export interface InstitutionsGroup {
  [name: string]: DataPoint[];
}

export type View = 'eu' | 'cn';


function App() {
  const [data, setData] = useState<DataPoint[]>([]);

  const availableCountries = useMemo(() => {
    const countries: {[country: string]: boolean} = {};
    data.forEach((dataPoint) => {
      if (!countries[dataPoint.Country]) {
        countries[dataPoint.Country] = true;
      }
    });
    return new Map(Object.keys(countries).map((key) => [key, true]));
  }, [data]);

  const mapPage = () => {
    return (
      <MapPage
        data={data}
        availableCountries={availableCountries}
      />
    );
  };

  useEffect(() => {
    fetch(
        `https://opensheet.elk.sh/1709WjIVxOGUM--H_FnRRlR9kJEwx2d798OjVOgXGRWY/data`,
    )
        .then((res) => res.json())
        .then((data: DataPoint[]) => {
          const mappedData = data.map((row, i) => {
            const dataPoint: DataPoint = {
              ...row,
              id: i.toString(),
              Category: getCategoryEnum(row.Category),
              coords: [Number(row.Longitude), Number(row.Latitude)],
            };
            return dataPoint;
          });
          setData(mappedData);
        });
  }, []);

  return (
    <div className="App">
      <Helmet>
        {/* <!-- HTML Meta Tags --> */}
        <title>Central Europe-East Asia EV Nexus Tracker</title>
        <meta
          name="description"
          content="The project studies the participation of East Asian EV carmakers in the V4 countries’ EV transition, and analyzes their strategies in navigating the local market and the political and economic dynamics in the V4 countries in attracting FDI in this sector."
        />
        <meta property="og:image" content="https://evnexustracker.ceias.eu/evnexus-tracker.png"/>
        {/* <!-- Google / Search Engine Tags --> */}
        <meta itemProp="name" content="Central Europe-East Asia EV Nexus Tracker" />
        <meta
          itemProp="description"
          content="The project studies the participation of East Asian EV carmakers in the V4 countries’ EV transition, and analyzes their strategies in navigating the local market and the political and economic dynamics in the V4 countries in attracting FDI in this sector."
        />
        <meta property="og:image" content="https://evnexustracker.ceias.eu/evnexus-tracker.png"/>

        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Central Europe-East Asia EV Nexus Tracker" />
        <meta
          property="og:description"
          content="The project studies the participation of East Asian EV carmakers in the V4 countries’ EV transition, and analyzes their strategies in navigating the local market and the political and economic dynamics in the V4 countries in attracting FDI in this sector."
        />
        <meta property="og:image" content="https://evnexustracker.ceias.eu/evnexus-tracker.png"/>
        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:title" content="Central Europe-East Asia EV Nexus Tracker"/>
        <meta name="twitter:description" content="A database of interactions between European academic institutions and Chinese entities. It was created by CEIAS and partners from investigated countries."/>
        <meta name="twitter:image" content="https://evnexustracker.ceias.eu/evnexus-tracker.png"/>
      </Helmet>
      <Routes>
        <Route path="/team" element={<Team/>} />
        <Route path="/crowdsourcing" element={<Crowdsourcing/>} />
        <Route path="/articles" element={<Articles/>} />
        <Route path="/articles/:id" element={<Articles/>} />
        <Route path="/contact" element={<Contact/>} />
        <Route path="/about" element={<About/>} />
        <Route path="/map/:id" element={mapPage()} />
        <Route path="/map" element={mapPage()} />
        <Route path="*" element={<Navigate to="/map" />} />
      </Routes>
      <CookieConsent style={{
        zIndex: 100000,
        left: 'auto',
        width: 'auto',
        right: 0,
        margin: '20px',
        borderRadius: '5px',
      }}>
        This website uses cookies to enhance the user experience.
      </CookieConsent>
    </div>
  );
}

export default App;
